import { request } from '../request';
import { getClientId } from '@/utils/clientId';

/**
 * 验证并绑定邮箱
 * PUT /accounts/email/bind
 * Bind an email address after verifying its code.
 *
 * @param data 请求体参数 (email, verification_code)
 */
export function fetchBindEmail(data: Api.Account.BindEmailRequest) {
  return request<Api.Account.BindEmailResponse>({
    url: '/accounts/email/bind',
    method: 'put',
    data
  });
}

/**
 * 发送邮箱验证码
 * POST /accounts/email/bind/code
 * 用户绑定邮箱时输入邮箱 发送验证码
 *
 * @param data 请求体参数 (email)
 */
export function fetchSendEmailBindingCode(data: Api.Account.SendEmailBindingCodeRequest) {
  return request<Api.Account.SendEmailBindingCodeResponse>({
    url: '/accounts/email/bind/code',
    method: 'post',
    data
  });
}

/**
 * 发送当前手机号验证码
 * POST /accounts/phone/old/verify/code
 */
export function fetchSendOldPhoneVerifyCode() {
  return request<Api.Account.SendPhoneBindingCodeResponse>({
    url: '/accounts/phone/old/verify/code',
    method: 'post'
  });
}

/**
 * 验证当前手机号并获取换绑临时 token
 * POST /accounts/phone/old/verify
 *
 * @param data 请求体参数 (verification_code)
 */
export function fetchVerifyOldPhone(data: Api.Account.VerifyOldPhoneRequest) {
  return request<Api.Account.VerificationTokenResponse>({
    url: '/accounts/phone/old/verify',
    method: 'post',
    data
  });
}

/**
 * 换绑当前账号手机号
 * PUT /accounts/phone
 * 使用旧手机号验证成功后获得的一次性 token，并校验新手机号验证码后更新当前账号；更新成功会使该账号所有登录会话失效。
 *
 * @param data 请求体参数 (old_verification_token, phone, verification_code)
 */
export function fetchChangePhone(data: Api.Account.ChangePhoneRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: '/accounts/phone',
    method: 'put',
    data
  });
}

/**
 * 发送手机绑定验证码
 * POST /accounts/phone/bind/code
 * 用户换绑/绑定手机号时发送验证码
 *
 * @param data 请求体参数 (phone)
 */
export function fetchSendPhoneBindingCode(data: Api.Account.SendPhoneBindingCodeRequest) {
  return request<Api.Account.SendPhoneBindingCodeResponse>({
    url: '/accounts/phone/bind/code',
    method: 'post',
    data
  });
}

/**
 * 企业账号登录或注册
 * POST /accounts/enterprise/login
 * 首次使用企业手机号登录时自动创建企业账号，首次登录必须同意用户协议。
 *
 * @param data 请求体参数 (phone, verification_code, agreement_accepted, device_id)
 */
export function fetchEnterpriseLogin(data: Api.Account.AccountLoginRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: '/accounts/enterprise/login',
    method: 'post',
    data
  });
}

/**
 * 获取当前前台账号
 * GET /accounts/me
 * 获取当前登录的前台账号信息
 */
export function fetchGetAccountMe() {
  return request<Api.Account.AccountResponse>({
    url: '/accounts/me',
    method: 'get'
  });
}

/**
 * 个人账号登录或注册
 * POST /accounts/personal/login
 * 首次使用个人手机号登录时自动创建个人账号，首次登录必须同意用户协议。
 *
 * @param data 请求体参数 (phone, verification_code, agreement_accepted, device_id)
 */
export function fetchPersonalLogin(data: Api.Account.AccountLoginRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: '/accounts/personal/login',
    method: 'post',
    data
  });
}

/**
 * 退出当前前台登录设备
 * POST /accounts/sessions/logout
 */
export function fetchLogoutSession() {
  return request<Record<string, any>>({
    url: '/accounts/sessions/logout',
    method: 'post'
  });
}

/**
 * 退出全部前台登录设备
 * POST /accounts/sessions/logout-all
 */
export function fetchLogoutAllSessions() {
  return request<Record<string, any>>({
    url: '/accounts/sessions/logout-all',
    method: 'post'
  });
}

/**
 * 刷新前台登录会话
 * POST /accounts/sessions/refresh
 */
export function fetchRefreshSession() {
  return request<Record<string, any>>({
    url: '/accounts/sessions/refresh',
    method: 'post'
  });
}

/**
 * 微信小程序手机号快捷登录
 * POST /accounts/wechat-miniprogram/phone-login
 * 使用 wx.getPhoneNumber 返回的 phone_code 获取手机号，并按账号类型登录或注册。
 *
 * @param data 请求体参数 (account_type, phone_code, agreement_accepted, device_id)
 */
export function fetchWeChatMiniProgramPhoneLogin(data: Api.Account.WeChatMiniProgramPhoneLoginRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: '/accounts/wechat-miniprogram/phone-login',
    method: 'post',
    data
  });
}

/**
 * 微信小程序短信验证码登录
 * POST /accounts/wechat-miniprogram/sms-login
 *
 * @param data 请求体参数 (account_type, phone, verification_code, agreement_accepted, device_id)
 */
export function fetchWeChatMiniProgramSMSLogin(data: Api.Account.WeChatMiniProgramSMSLoginRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: '/accounts/wechat-miniprogram/sms-login',
    method: 'post',
    data
  });
}

/**
 * 更新当前帐户头像
 * PUT /accounts/avatar
 *
 * @param data 请求体参数 (avatar_asset_id)
 */
export function fetchUpdateAccountAvatar(data: Api.Account.UpdateAccountAvatarRequest) {
  return request<Api.Account.AccountResponse>({
    url: '/accounts/avatar',
    method: 'put',
    data
  });
}

/**
 * 发送登录手机号验证码
 * POST /accounts/login/code
 *
 * @param data 请求体参数 (phone)
 */
export function fetchSendLoginCode(data: Api.Account.SendLoginCodeRequest) {
  return request<Api.Account.SendLoginCodeResponse>({
    url: '/accounts/login/code',
    method: 'post',
    data: {
      client_id: getClientId(),
      ...data,
    }
  });
}

/**
 * 创建微信公众号关注/登录二维码
 * POST /accounts/wechat-official/qr-login-tickets
 *
 * @param data 请求体参数 (account_type: PERSONAL | ENTERPRISE)
 */
export function fetchCreateWeChatOfficialQrTicket(data?: Api.Account.WeChatOfficialQrTicketCreateRequest) {
  return request<Api.Account.WeChatOfficialQrTicketResponse>({
    url: '/accounts/wechat-official/qr-login-tickets',
    method: 'post',
    data
  });
}

/**
 * 查询微信公众号二维码状态
 * POST /accounts/wechat-official/qr-login-tickets/{ticket_id}/status
 */
export function fetchCheckWeChatOfficialQrStatus(ticketId: string, data: Api.Account.WeChatOfficialQrStatusRequest) {
  return request<Api.Account.WeChatOfficialQrStatusResponse>({
    url: `/accounts/wechat-official/qr-login-tickets/${encodeURIComponent(ticketId)}/status`,
    method: 'post',
    data
  });
}

/**
 * 兑换微信公众号登录会话
 * POST /accounts/wechat-official/qr-login-tickets/{ticket_id}/exchange
 */
export function fetchExchangeWeChatOfficialQrTicket(ticketId: string, data: Api.Account.WeChatOfficialQrExchangeRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: `/accounts/wechat-official/qr-login-tickets/${encodeURIComponent(ticketId)}/exchange`,
    method: 'post',
    data
  });
}

/**
 * 微信公众号登录绑定手机号
 * POST /accounts/wechat-official/bind-phone
 */
export function fetchBindWeChatOfficialPhone(data: Api.Account.BindWeChatOfficialPhoneRequest) {
  return request<Api.Account.AccountLoginResponse>({
    url: '/accounts/wechat-official/bind-phone',
    method: 'post',
    data
  });
}

/**
 * 修改当前账号昵称
 * PUT /accounts/nickname
 *
 * @param data 请求体参数 (nick_name)
 */
export function fetchUpdateAccountNickname(data: Api.Account.UpdateAccountNicknameRequest) {
  return request<Api.Account.UpdateAccountNicknameResponse>({
    url: '/accounts/nickname',
    method: 'put',
    data
  });
}



